import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstance } from 'react-ioc';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {DeleteOutlined, MoreOutlined, SendOutlined} from '@ant-design/icons';
import { OutputData } from '@editorjs/editorjs';
import { Button, DatePicker, Divider, Dropdown, Form, Input, Switch } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';

import Editor from '../../components/Editor/Editor';
import { SelectClient } from '../../components/Form/SelectClient/SelectClient';
import { SelectGroup } from '../../components/Form/SelectGroup/SelectGroup';
import { SelectStatus } from '../../components/Form/SelectStatus/SelectStatus';
import { SelectTeam } from '../../components/Form/SelectTeam/SelectTeam';
import { TextArea } from '../../components/Form/TextArea/TextArea';
import { IParam, Page } from '../../components/Page/Page';
import { Reports } from '../../components/Report/Reports';
import { Timers } from '../../components/Timer/TImers';
import {
    Enum_Imagegeneration_Status,
    useCreateImageGenerationMutation,
    useCreateTaskMutation,
    useTaskQuery,
    useUpdateTaskMutation,
} from '../../generated/graphql';
import { Store } from '../../model/store/Store';
import { goToTask } from '../../routes';
import { ImageUrl } from '../../shared/ImageUrl';

import cls from './Task.module.scss';

export interface IFormTask {
    name?: string;
    status: string;
    client?: string;
    group?: string;
    users: string[];
    content: OutputData;
}

export const Task = observer(() => {
    const url = useParams();
    const location = useLocation();
    const { auth, domain } = useInstance(Store);

    const [id, setId] = useState<string>(url.id!);
    const [showImageForm, setShowImageForm] = useState(false);
    const [imagePrompt, setImagePrompt] = useState<string>('');

    const [name, setName] = useState<string | undefined>(undefined);
    const [status, setStatus] = useState<string>(location.state?.status || 'New');
    const [client, setClient] = useState<string | undefined>(location.state?.client || undefined);
    const [group, setGroup] = useState<string | undefined>(location.state?.epic || undefined);
    const [users, setUsers] = useState<string[]>([auth.id!]);
    const [deadline, setDeadline] = useState<dayjs.Dayjs | null>(null);
    const [content, setContent] = useState<OutputData>({
        time: Date.now(),
        blocks: [],
    });

    const query = useTaskQuery({
        variables: {
            id: id,
        },
        skip: id === 'new',
        onCompleted: (data) => {
            setName(data.task?.data?.attributes?.name!);
            setStatus(data.task?.data?.attributes?.status!);
            setClient(data.task?.data?.attributes?.company?.data?.id!);
            setGroup(data.task?.data?.attributes?.epic!.data?.id!);
            setUsers(_.map(data.task?.data?.attributes?.users?.data, (user) => user.id!));
            setContent(data.task?.data?.attributes?.content!);
            setDeadline(data.task?.data?.attributes?.deadline ? dayjs(data.task?.data?.attributes?.deadline) : null);
        },
    });

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [updateTask, updateTaskData] = useUpdateTaskMutation();
    const [createTask, createTaskData] = useCreateTaskMutation();
    const [createImageGeneration] = useCreateImageGenerationMutation();

    const goBack = () => {
        navigate(-1);
    };

    const handleCoverClick = () => {
        setShowImageForm(!showImageForm);
    };

    const onGenerate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!imagePrompt || id === 'new') return;

        await createImageGeneration({
            variables: {
                data: {
                    promt: imagePrompt,
                    task: id,
                    status: Enum_Imagegeneration_Status.Created_1,
                },
            },
            onCompleted: () => {
                setShowImageForm(false);
                setImagePrompt('');
                query.refetch();
            },
        });
    };

    const onUpdateName = async (value: string) => {
        setName(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        name: value,
                    },
                },
            });
        } else {
            if (value !== '') {
                await createTask({
                    variables: {
                        data: {
                            name: value,
                            content: content,
                            status: status,
                            company: client,
                            epic: group,
                            users: users,
                            deadline: deadline ? deadline.toISOString() : undefined,
                            domain: domain.selectedDomain,
                        },
                    },
                    onCompleted: (res) => {
                        setId(res.createTask?.data?.id!);
                        navigate(goToTask(res.createTask?.data?.id!), { replace: true });
                    },
                });
            }
        }
    };

    const onUpdateStatus = async (value: string) => {
        setStatus(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        status: value,
                    },
                },
            });
        }
    };

    const onUpdateClient = async (value: string) => {
        setClient(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        company: value,
                    },
                },
            });
        }
    };

    const onUpdateGroup = async (value: string) => {
        setGroup(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        epic: value,
                    },
                },
            });
        }
    };

    const onUpdateUsers = async (value: string[]) => {
        setUsers(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        users: value,
                    },
                },
            });
        }
    };

    const onUpdateDeadline = async (value: dayjs.Dayjs | null) => {
        setDeadline(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        deadline: value ? value.format('YYYY-MM-DD') : null,
                    },
                },
            });
        }
    };

    const onUpdateContent = async (value: OutputData) => {
        setContent(value);
        if (id !== 'new') {
            await updateTask({
                variables: {
                    id: id!,
                    data: {
                        content: value,
                    },
                },
            });
        }
    };

    const onDeleteTask = async () => {
        await updateTask({
            variables: {
                id: id!,
                data: {
                    enabled: false,
                },
            },
            onCompleted: () => {
                goBack();
            },
        });
    };

    const onUpdateArchive = async (value: boolean) => {
        await updateTask({
            variables: {
                id: id!,
                data: {
                    archive: value
                }
            },
            onCompleted: () => {
                if (value) {
                    goBack();
                } else {
                    query.refetch();
                }
            }
        })
    }

    const menu = {
        items: [
            {
                key: 'delete',
                label: t('Delete'),
                danger: true,
                icon: <DeleteOutlined />,
                onClick: id !== 'new' ? onDeleteTask : goBack,
            },
        ],
    };

    const params: IParam[] = [
        {
            key: 'status',
            label: t('Status')!,
            component: (
                <SelectStatus
                    value={status}
                    bordered={false}
                    variants={'borderless'}
                    className={cls.select}
                    onChange={(value: string) => onUpdateStatus(value)}
                />
            ),
        },
        {
            key: 'archive',
            label: t('Archive')!,
            component: (
                <Switch
                    disabled={status === 'Focus'}
                    // loading={updateTaskData.loading}
                    checked={query.data?.task?.data?.attributes?.archive}
                    onChange={(value: boolean) => onUpdateArchive(value)}
                />
            ),
        },
        {
            key: 'client',
            label: t('Client')!,
            component: (
                <SelectClient
                    value={client}
                    bordered={false}
                    variants={'borderless'}
                    className={cls.select}
                    onChange={(value: string) => onUpdateClient(value)}
                    onCreate={(value: string) => onUpdateClient(value)}
                />
            ),
        },
        {
            key: 'epic',
            label: t('Group')!,
            component: (
                <SelectGroup
                    value={group}
                    bordered={false}
                    variants={'borderless'}
                    className={cls.select}
                    onChange={(value: string) => onUpdateGroup(value)}
                    onCreate={(value: string) => onUpdateGroup(value)}
                />
            ),
        },
        {
            key: 'team',
            label: t('Team')!,
            component: (
                <SelectTeam
                    value={users}
                    bordered={false}
                    variants={'borderless'}
                    className={cls.select}
                    onChange={(value: string[]) => onUpdateUsers(value)}
                />
            ),
        },
        {
            key: 'deadline',
            label: t('Deadline')!,
            component: (
                <DatePicker
                    value={deadline}
                    bordered={false}
                    className={cls.select}
                    format="DD.MM.YYYY"
                    onChange={(value) => onUpdateDeadline(value)}
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            key: 'div',
            component: <Divider style={{ margin: '0' }} />,
            block: true,
        },
        ...(domain.hasFocus() && id !== 'new'
            ? [
                  {
                      key: 'focus',
                      label: t('Focus')!,
                      labelTop: true,
                      component: <Timers taskId={id!} />,
                  },
                  {
                      key: 'div-focus',
                      component: <Divider style={{ margin: '0' }} />,
                      block: true,
                  },
              ]
            : []),
        ...(id !== 'new'
            ? [
                  {
                      key: 'report',
                      label: t('Reports')!,
                      labelTop: true,
                      component: <Reports taskId={id!} />,
                  },
                  {
                      key: 'div-report',
                      component: <Divider style={{ margin: '0 0 16px 0' }} />,
                      block: true,
                  },
              ]
            : []),
    ];

    if (id !== 'new' && query.loading) {
        return null;
    }

    return (
        <Page
            cover={query.data?.task?.data?.attributes?.cover?.data ? 
                <>
                    <img 
                        className={cls.cover} 
                        src={ImageUrl(query.data?.task?.data?.attributes.cover.data.attributes?.url!)} 
                        alt={name} 
                        onClick={handleCoverClick}
                        style={{ cursor: 'pointer' }}
                    />
                    {showImageForm && id !== 'new' && (
                <div className={cls.imageForm}>
                    <Form onFinish={onGenerate} layout="inline">
                        <Form.Item style={{ flex: 1, marginRight: 8 }}>
                            <Input
                                placeholder={t('Text for image generation')!}
                                value={imagePrompt}
                                onChange={(e) => setImagePrompt(e.target.value)}
                                autoFocus
                            />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" icon={<SendOutlined/>} />
                        </Form.Item>
                    </Form>
                </div>
            )}
                </>
                : undefined}
            title={<TextArea value={name} onUpdate={(value: string) => onUpdateName(value)} />}
            control={
                <Dropdown menu={menu} trigger={['click']}>
                    <Button icon={<MoreOutlined />} loading={updateTaskData.loading || createTaskData.loading} />
                </Dropdown>
            }
        >
            <Page.Content>
                <Page.Params data={params} />
            </Page.Content>

            <div className={cls.editor}>
                {id !== 'new' && !query.loading && query.data?.task?.data && (
                    <Editor
                        data={query.data.task.data.attributes?.content!}
                        onUpdate={(value) => {
                            onUpdateContent(value);
                        }}
                    />
                )}
            </div>
        </Page>
    );
});
